import React, { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  RefreshCw,
  Power,
  Loader2,
  AlertTriangle,
  Globe,
  ChevronDown,
} from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

const severityColor = {
  critical:
    "bg-red-500/10 text-red-400 border-red-500/30 shadow-[0_0_8px_rgba(239,68,68,0.2)]",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  low: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  none: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

const MonitoredDomains = () => {
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [exposuresById, setExposuresById] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.getMonitoredAssets("domain");
      setDomains(res.assets || []);
    } catch (err) {
      toast.error("Failed to load monitored domains");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.addMonitoredAsset({
        value: newDomain,
        assetType: "domain",
        label: newLabel,
      });
      toast.success("Domain added — initial scan started");
      setNewDomain("");
      setNewLabel("");
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const handleScan = async (id) => {
    try {
      toast.info("Scan started…");
      await api.scanMonitoredAsset(id);
      toast.success("Scan complete");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Scan failed");
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.toggleMonitoredAsset(id);
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this domain from monitoring?")) return;
    try {
      await api.deleteMonitoredAsset(id);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!exposuresById[id]) {
      const res = await api.getAssetExposures(id);
      setExposuresById((p) => ({ ...p, [id]: res.exposures }));
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0C0E12] text-gray-300 p-6 lg:p-8"
      style={{
        backgroundImage:
          "radial-gradient(circle at 10% 0%, rgba(6,182,212,0.06), transparent 40%), radial-gradient(circle at 90% 100%, rgba(139,92,246,0.05), transparent 40%)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Globe size={14} className="text-cyan-400" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-cyan-400 font-mono">
                Asset Monitoring
              </span>
            </div>
            <h1 className="text-3xl font-bold text-white">
              Monitored <span className="text-cyan-400">Domains</span>
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              Real-time breach exposure tracking across your digital identity
              surface.
            </p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-400/60 text-cyan-300 rounded-md font-mono text-xs tracking-wider uppercase transition-all hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]"
          >
            <Plus size={14} /> Add Domain
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-cyan-400" size={40} />
          </div>
        ) : domains.length === 0 ? (
          <div className="bg-[#12151C] rounded-xl p-12 text-center border border-white/5">
            <Globe className="mx-auto text-gray-600 mb-4" size={40} />
            <p className="text-gray-400 mb-4 font-mono text-sm">
              // No domains under surveillance
            </p>
            <button
              onClick={() => setShowAdd(true)}
              className="px-4 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 rounded-md font-mono text-xs tracking-wider uppercase"
            >
              Initialize Monitoring
            </button>
          </div>
        ) : (
          <div className="bg-[#12151C] rounded-xl border border-white/5 overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.5)]">
            <table className="w-full">
              <thead className="bg-black/40 border-b border-white/5">
                <tr className="text-left text-[10px] font-mono tracking-[0.2em] text-gray-500 uppercase">
                  <th className="px-6 py-3.5">Domain</th>
                  <th className="px-6 py-3.5">Risk</th>
                  <th className="px-6 py-3.5">Exposures</th>
                  <th className="px-6 py-3.5">Last Scan</th>
                  <th className="px-6 py-3.5">Status</th>
                  <th className="px-6 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {domains.map((e) => (
                  <React.Fragment key={e.id}>
                    <tr
                      className="hover:bg-cyan-500/5 cursor-pointer transition-colors group"
                      onClick={() => toggleExpand(e.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Globe
                            size={14}
                            className="text-cyan-400 flex-shrink-0"
                          />
                          <div>
                            <div className="text-white font-mono text-sm font-medium">
                              {e.value}
                            </div>
                            {e.label && (
                              <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                                {e.label}
                              </div>
                            )}
                          </div>
                          <ChevronDown
                            size={12}
                            className={`text-gray-600 ml-auto transition-transform ${expandedId === e.id ? "rotate-180" : ""}`}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-md border font-mono uppercase tracking-wider ${severityColor[e.riskLevel] || severityColor.none}`}
                        >
                          {e.riskLevel || "none"}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-mono font-semibold text-cyan-300">
                        {e.exposureCount}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500 font-mono">
                        {e.lastScannedAt
                          ? new Date(e.lastScannedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-xs font-mono flex items-center gap-1.5 ${e.monitoringEnabled ? "text-emerald-400" : "text-gray-500"}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${e.monitoringEnabled ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)] animate-pulse" : "bg-gray-600"}`}
                          />
                          {e.monitoringEnabled ? "ACTIVE" : "PAUSED"}
                        </span>
                        {!e.isVerified && (
                          <div className="text-[10px] text-amber-500 mt-1 font-mono">
                            UNVERIFIED
                          </div>
                        )}
                      </td>
                      <td
                        className="px-6 py-4"
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleScan(e.id)}
                            title="Scan now"
                            className="p-2 hover:bg-cyan-500/10 rounded transition-colors"
                          >
                            <RefreshCw size={14} className="text-cyan-400" />
                          </button>
                          <button
                            onClick={() => handleToggle(e.id)}
                            title="Toggle"
                            className="p-2 hover:bg-white/5 rounded transition-colors"
                          >
                            <Power size={14} className="text-gray-400" />
                          </button>
                          <button
                            onClick={() => handleDelete(e.id)}
                            title="Delete"
                            className="p-2 hover:bg-red-500/10 rounded transition-colors"
                          >
                            <Trash2 size={14} className="text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === e.id && (
                      <tr>
                        <td
                          colSpan={6}
                          className="bg-black/40 px-6 py-5 border-t border-cyan-500/10"
                        >
                          <h4 className="font-mono text-[10px] tracking-[0.2em] uppercase text-cyan-400 mb-3">
                            // Breach Exposure Records
                          </h4>
                          {!exposuresById[e.id] ? (
                            <Loader2
                              className="animate-spin text-cyan-400"
                              size={18}
                            />
                          ) : exposuresById[e.id].length === 0 ? (
                            <div className="text-sm text-emerald-400 font-mono flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                              No breach exposures detected
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {exposuresById[e.id].map((ex) => (
                                <div
                                  key={ex.id}
                                  className="flex items-start gap-3 bg-[#12151C] p-3 rounded-lg border border-white/5 hover:border-cyan-500/20 transition-colors"
                                >
                                  <AlertTriangle
                                    size={18}
                                    className="text-amber-400 mt-0.5 flex-shrink-0"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-white">
                                      {ex.breach?.title}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono mt-0.5">
                                      {ex.breach?.breachDate
                                        ? new Date(
                                            ex.breach.breachDate,
                                          ).toLocaleDateString()
                                        : ""}{" "}
                                      •{" "}
                                      {(ex.exposedDataClasses || []).join(", ")}
                                    </div>
                                  </div>
                                  <span
                                    className={`text-[10px] px-2 py-0.5 rounded-md border font-mono uppercase tracking-wider ${severityColor[ex.severity] || severityColor.none}`}
                                  >
                                    {ex.severity}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showAdd && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowAdd(false)}
          >
            <form
              onSubmit={handleAdd}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#12151C] rounded-2xl p-6 w-full max-w-md border border-cyan-500/20 shadow-[0_0_40px_rgba(6,182,212,0.15)] relative"
            >
              <div className="absolute top-0 left-0 w-10 h-10 border-t border-l border-cyan-400/40 rounded-tl-2xl pointer-events-none" />
              <div className="absolute top-0 right-0 w-10 h-10 border-t border-r border-cyan-400/40 rounded-tr-2xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b border-l border-cyan-400/40 rounded-bl-2xl pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-10 h-10 border-b border-r border-cyan-400/40 rounded-br-2xl pointer-events-none" />

              <div className="flex items-center gap-2 mb-4">
                <Globe size={14} className="text-cyan-400" />
                <h3 className="text-[10px] tracking-[0.3em] uppercase text-cyan-400 font-mono">
                  Add Asset to Surveillance
                </h3>
              </div>
              <div className="space-y-3">
                <input
                  type="text"
                  required
                  placeholder="example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  className="w-full px-3 py-2.5 bg-black/40 border border-white/10 focus:border-cyan-500/50 rounded-lg text-white font-mono text-sm focus:outline-none focus:shadow-[0_0_15px_rgba(6,182,212,0.15)] transition-all placeholder-gray-600"
                />
                <input
                  type="text"
                  placeholder="Optional label (e.g. Primary domain)"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="w-full px-3 py-2.5 bg-black/40 border border-white/10 focus:border-cyan-500/50 rounded-lg text-white text-sm focus:outline-none focus:shadow-[0_0_15px_rgba(6,182,212,0.15)] transition-all placeholder-gray-600"
                />
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="px-4 py-2 border border-white/10 hover:border-white/20 text-gray-400 rounded-md font-mono text-xs uppercase tracking-wider transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-400/60 text-cyan-300 rounded-md font-mono text-xs uppercase tracking-wider disabled:opacity-50 transition-all hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]"
                >
                  {saving ? "Adding…" : "Confirm"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitoredDomains;
